import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { AgentConfigOptions, ApmBase } from '@elastic/apm-rum'
import { ApmService } from '@elastic/apm-rum-angular'
import * as FullStory from '@fullstory/browser'
import { Analytics, AnalyticsInstance } from 'analytics'
import { filter, first } from 'rxjs/operators'

import { Enums, Profile } from '@cyborg-monorepo/hunter/common'

import { VerityEnvironmentService } from '../../environments/verity-environment.service'
import { AuthService } from '../auth/auth.service'
import { FrameDetectionService } from '../modules/shared/services'

import { ChurnzeroConfig, churnzeroPlugin } from './churnzero.analytics'
import { hunterApiPlugin } from './hunter.analytics'

export interface TrackingServiceConfig {
  enabled: boolean
  devMode?: boolean
  trackers: {
    elastic: AgentConfigOptions
    fullstory: Parameters<typeof FullStory.init>[0]
    churnzero: Omit<ChurnzeroConfig, 'httpClient'>
  }
}

export interface TrackingAnalyticsIdentifyTraits {
  email: string
  displayName: string
  company: string
  companyStatus: string
  verityCustomerCode: string
  companyHuntModuleAccess: boolean
  domain: 'verity' | 'hunter'
}

export import TrackingEventEnum = Enums.TrackingEventEnum
/**
 * Configure and initialize user tracking modules, and subscribe to any events to be tracked
 */
@Injectable()
export class TrackingService {
  private frameDetectionService = inject(FrameDetectionService)
  private router = inject(Router)
  private apmService = inject(ApmService)
  private authService = inject(AuthService)
  private httpClient = inject(HttpClient)
  private verityEnvironmentService = inject(VerityEnvironmentService)

  private apm: ApmBase
  private user: Profile

  private analytics: AnalyticsInstance

  // Stubbed here to allow taking in config from module symbol .forRoot pattern in the future, and not directly from environment
  private config: TrackingServiceConfig =
    this.verityEnvironmentService.trackingConfig

  public init(): void {
    if (this.config.enabled) {
      // After user is first authenticated, activate tracking modules
      this.authService.user$
        .pipe(
          filter((user) => !!user),
          first(),
        )
        .subscribe(async (user) => {
          if (user !== this.user) {
            this.user = user
            this.initTrackingModules()
            this.identifyUser(user)
            this.subscribeToRouteChanges()
            this.trackEvent(TrackingEventEnum.AUTH_LOGIN)
          }
        })
    }
  }

  /**
   * Allow external event tracking across app
   */
  public trackEvent(
    eventName: TrackingEventEnum,
    eventMetadata?: object,
  ): void {
    if (this.config.enabled) {
      this.analytics.track(eventName, eventMetadata)
    }
  }

  /**
   * Start all tracking modules
   */
  private initTrackingModules(): void {
    const plugins = [
      hunterApiPlugin({
        httpClient: this.httpClient,
        hunterApiUrl: this.verityEnvironmentService.apiUrl,
      }),
      churnzeroPlugin({
        ...this.config.trackers.churnzero,
        httpClient: this.httpClient,
      }),
    ]

    this.analytics = Analytics({
      app: 'hunter',
      // Only use internal hunter tracking in devMode
      plugins: this.config.devMode ? [plugins[0]] : plugins,
    })
    FullStory.init(this.config.trackers.fullstory)
    // 🚨 DO NOT REMOVE: Initializes Elastic APM. Required for performance monitoring and error tracking.
    // This is the only place we can initialize the APM instance, as it needs to be done before any other code runs
    // and before the Angular app is bootstrapped
    this.apm = this.apmService.init(this.config.trackers.elastic)
  }

  /**
   * Identify user across all tracking services
   */
  private identifyUser(user: Profile): void {
    // Namespace user IDs when in dev mode to avoid collisions and live account replacements
    const userId: string = this.config.devMode
      ? `DEV-${user.id.toString()}`
      : user.id.toString()
    const userDisplayName = `${user.firstName} ${user.lastName}`

    const analyticsTraits: TrackingAnalyticsIdentifyTraits = {
      email: user.email,
      displayName: userDisplayName,
      company: user.company.name,
      companyStatus: user.company.status,
      verityCustomerCode: user.company.verityCustomerCode,
      companyHuntModuleAccess: user.company.huntModuleAccess,
      domain: this.frameDetectionService.isIframe() ? 'verity' : 'hunter',
    }

    this.analytics.identify(userId, analyticsTraits)

    // Elastic APM
    this.apm.setUserContext({
      email: user.email,
      username: userDisplayName,
      id: userId,
    })
    this.apm.addLabels({
      company: user.company.name,
      companyStatus: user.company.status,
      verityCustomerCode: user.company.verityCustomerCode,
      companyHuntModuleAccess: user.company.huntModuleAccess,
      domain: this.frameDetectionService.isIframe() ? 'verity' : 'hunter',
    })

    // Fullstory
    FullStory.identify(userId, analyticsTraits)
  }

  /**
   * Subscribe to and report on route / page changes
   */
  private subscribeToRouteChanges(): void {
    this.router.events
      .pipe(filter<NavigationEnd>((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.analytics.page()
      })
  }
}
